import { Subscription, TimeoutError } from 'rxjs';
import { QueueService } from './../../queue/queue/queue.service';
import { JoinQueueRequest } from './../../core/models/JoinQueueRequest';
import { UserService } from './../../core/user/user.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { mapLoginResp, User } from 'src/app/core/user/user.model';
import { ProfileService } from '../services/profile.service';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UtilsService } from 'src/app/core/services/utils.service';
import { ErrorCode } from 'src/app/core/enums/ErrorCode';
import { SignupForm } from '../models/SignupForm';
import { SessionStorageService } from 'src/app/shared/storage/session-storage.service';
import { NavigationService } from 'src/app/core/services/navigation.service';

@Component({
  selector: 'app-complete-profile',
  templateUrl: './complete-profile.component.html',
  styleUrls: ['./complete-profile.component.scss'],
  providers: [ProfileService]
})
export class CompleteProfileComponent implements OnInit, OnDestroy {
  fgroup: UntypedFormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.pattern(/\S/)]],
    email: ['', [Validators.email, Validators.pattern(this.utilsService.emailPattern)]],
    dob: ['']
  });
  currentUser: User = null;
  otpCodeState: string = '';
  profileError: HttpErrorResponse = null;
  errorCode = ErrorCode;
  updateProfileResult: any = null;
  isQueue : boolean = false;
  isQueueRegister : boolean = false;
  displayRegisterFailedMsg : boolean = false;
  joinQueueRequest : JoinQueueRequest = null;
  canRegister : boolean = false;
  maxYearRange: number;
  maxDate: Date = new Date();
  displayError: boolean = false;
  errorField: string;
  errorMessage: string;
  isRequiredFeild: boolean = false;
  nameInputSub$ : Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private profileService: ProfileService,
    private utilsService: UtilsService,
    private route : ActivatedRoute,
    private queueService : QueueService,
    private sessionStorageService: SessionStorageService,
    private navigationService: NavigationService
  ) {
    this.nameInputSub$ = this.fgroup.get('name').valueChanges.subscribe(val => {
      this.isRequiredFeild = false;
    })
  }

  async ngOnInit(): Promise<void> {
    const currentDate = new Date();
    this.maxYearRange = currentDate.getFullYear();
    this.currentUser = this.userService.getCustomer();
    this.otpCodeState = history.state?.otpCodeState;
    this.isQueue = this.route.snapshot.data['isQueue'] ? this.route.snapshot.data['isQueue'] : false;
    this.canRegister = this.queueService.getCanQueueRegister();

    this.f.email.statusChanges.subscribe(formStatus => {
      if (formStatus === 'INVALID') {
        this.displayError = false
      }
    });
  }

  ngOnDestroy(): void {
    this.queueService.removeCanQueueRegisterFlag();
    this.nameInputSub$?.unsubscribe();
  }

  private getFormControl(formControlName: string) {
    return this.fg.controls[formControlName];
  }

  async onSubmit() {
    if (this.fg.invalid) {
      let phoneNo = this.getFormControl('name')?.value || '';
      phoneNo = phoneNo.trim();
      if(phoneNo.length === 0) {
        this.isRequiredFeild = true;
      }
      return;
    }

    let user: User = {
      name: this.fgroup.value['name'],
      email: this.fgroup.value['email'],
      dob: this.fgroup.value['dob']
    };

    if(this.isQueue){
      // get join request from session storage
      let queueRequest = {} as JoinQueueRequest;
      queueRequest = JSON.parse(this.sessionStorageService.getItem("queueRequest"));
      queueRequest.customerName = user.name;

      if(!this.isQueueRegister){
        await this.queueService.onJoinQueueInit(queueRequest);
      }
      else{
        this.sessionStorageService.setItem("queueRequest", JSON.stringify(queueRequest));
        await this.onQueueRegister(queueRequest, user.email);
      }
    }
    else{
      this.updateProfileResult = await this.profileService.onUpdateProfile(user, true);

      if(this.updateProfileResult instanceof TimeoutError){
        let errorObj = new HttpErrorResponse({
          status: 500,
          statusText: "API Timeout"
        });
        this.profileError = errorObj;
      } else if (this.updateProfileResult instanceof HttpErrorResponse) {
        if (this.updateProfileResult?.error?.errorCode === this.errorCode.UserRegisterConflict_409) {
          this.displayError = true;
          this.errorField = this.updateProfileResult?.error?.errorData[0]?.errorField ? this.updateProfileResult?.error?.errorData[0]?.errorField : 'label.email';
          this.errorMessage = this.updateProfileResult?.error?.errorData[0]?.errorMessage ? this.updateProfileResult?.error?.errorData[0]?.errorMessage : '';
        }
      } else {
        this.navigationService.navigate(["setup-password"], { replaceUrl: true });
      }
    }
  }

  get fg() { return this.fgroup; }
  get f() { return this.fg.controls; }

  onTickRegister(event: any){
    this.isQueueRegister = event.target.checked? true: false;
  }

  async onQueueRegister(queueRequest : JoinQueueRequest, email : string, ){
    const signupForm : SignupForm = {
      mobileNo: queueRequest.mobileNo,
      name: queueRequest.customerName,
      email: email ? email : undefined,
      dob: this.fgroup.value['dob'] ? this.fgroup.value['dob'] : undefined,
      otpCode: queueRequest.otpCode,
      isMobileVerified: true
    }

    let signupResult = await this.userService.signup({ ...signupForm });

    // need to add checking for http error
    if(signupResult instanceof HttpErrorResponse){
      this.joinQueueRequest  = queueRequest;
      this.displayRegisterFailedMsg = true;
    }
    else{
      await this.userService.updateToken(signupResult);
      let customerProfile = await this.userService.getProfile(false);
      let convertedTokenResponse = mapLoginResp(signupResult);
      this.userService.add({
        ...convertedTokenResponse,
        id: customerProfile.customerId,
        ...customerProfile
      } as User);

      this.navigationService.navigate(["setup-password"]);
    }
  }

  closeFailedPopup(){
    this.displayRegisterFailedMsg = false;
  }

  async proceedToQueue(){
    this.displayRegisterFailedMsg = false;
    await this.queueService.onJoinQueueInit(this.joinQueueRequest);
  }
}
